import React from 'react';

import { getBillingFormattedDate, getFormattedDate, parseToDate } from '../../../../utils/date';
import getDeviceStatusLabel from '../../OrganizationMemberList/utils/getDeviceStatusLabel';
import { DeviceInfo, DeviceStatus, DeviceStatusList } from '../../types';
import { DetailItem, DeviceStatusLineItem } from './DeviceLogLineItem';

const getDeviceTimestamp = (status: DeviceStatus, timestamp?: string | null) => {
    if (!timestamp) {
        return 'N/A';
    }

    if (status === 'ACTIVATED' || status === 'CANCELLED') {
        return getBillingFormattedDate(timestamp, 'MMM d, yyyy');
    }

    return getFormattedDate(parseToDate(timestamp), 'MMM d, yyyy');
};

const formatDeliveryDate = (date: string, label: string): DetailItem => ({
    label,
    value: getFormattedDate(parseToDate(date), 'MMM d, yyyy (HH:mm)')
});

const createFulfillmentDetails = (
    fulfillmentDetails: DeviceStatusList['fulfillment_details']
): DetailItem[] => {
    if (!fulfillmentDetails?.length) {
        return [];
    }

    const firstFulfillment = fulfillmentDetails[0];
    const details: DetailItem[] = [];

    // Always add serial number
    if (firstFulfillment.device_serial) {
        details.push({
            label: 'Serial',
            value: firstFulfillment.device_serial
        });
    }

    // Add tracking information if available
    if (firstFulfillment.tracking_number) {
        details.push({
            label: 'Tracking',
            value: firstFulfillment.tracking_number,
            url: firstFulfillment.tracking_url
        });
    }

    // Add delivery information (prioritize delivered_at over expected)
    if (firstFulfillment.delivered_at) {
        details.push(formatDeliveryDate(firstFulfillment.delivered_at, 'Delivered at'));
    } else if (firstFulfillment.delivery_expected_at) {
        details.push(
            formatDeliveryDate(firstFulfillment.delivery_expected_at, 'Expected delivery at')
        );
    }

    return details;
};

const createExchangeDetails = (deviceStatus: DeviceStatusList): DetailItem[][] => {
    const replacementDetails: DetailItem[] = [];
    const returnDetails: DetailItem[] = [];

    // Replacement device details
    if (deviceStatus.exchange_fulfillment_details?.length) {
        const exchangeFulfillment = deviceStatus.exchange_fulfillment_details[0];

        if (exchangeFulfillment.device_serial) {
            replacementDetails.push({
                label: 'Serial',
                value: exchangeFulfillment.device_serial
            });
        }

        if (exchangeFulfillment.tracking_number) {
            replacementDetails.push({
                label: 'Tracking',
                value: exchangeFulfillment.tracking_number,
                url: exchangeFulfillment.tracking_url
            });
        }

        // Add delivery information for replacement
        if (exchangeFulfillment.delivered_at) {
            replacementDetails.push(
                formatDeliveryDate(exchangeFulfillment.delivered_at, 'Delivered at')
            );
        } else if (exchangeFulfillment.delivery_expected_at) {
            replacementDetails.push(
                formatDeliveryDate(exchangeFulfillment.delivery_expected_at, 'Expected delivery at')
            );
        }
    }

    // Return device details
    if (deviceStatus.return_serial) {
        returnDetails.push({
            label: 'Serial',
            value: deviceStatus.return_serial
        });
    }

    if (deviceStatus.return_tracking_number) {
        returnDetails.push({
            label: 'Tracking',
            value: deviceStatus.return_tracking_number
        });
    }

    if (deviceStatus.return_label_url) {
        returnDetails.push({
            label: 'Return label',
            value: 'Download',
            url: deviceStatus.return_label_url
        });
    }

    return [replacementDetails, returnDetails];
};

const getDeviceStatusTitle = (deviceStatus: DeviceStatusList): string => {
    const isOrderedWithoutTracking =
        deviceStatus.device_status === 'ORDERED' &&
        !deviceStatus.fulfillment_details?.[0]?.tracking_number;

    return isOrderedWithoutTracking
        ? 'Ordered (not shipped)'
        : getDeviceStatusLabel(deviceStatus.device_status);
};

export const usePreparedDeviceStatusLogs = (
    currentDevice: DeviceInfo | undefined
): DeviceStatusLineItem[] => {
    return React.useMemo(() => {
        if (!currentDevice?.device_statuses) {
            return [];
        }

        return currentDevice.device_statuses
            .filter((deviceStatus) => deviceStatus.status_timestamp !== null)
            .map((deviceStatus: DeviceStatusList): DeviceStatusLineItem => {
                if (
                    deviceStatus.device_status === 'EXCHANGED' ||
                    deviceStatus.device_status === 'CANCELLED'
                ) {
                    const exchangeDetails = createExchangeDetails(deviceStatus);

                    const hasUnreturnedDevice =
                        deviceStatus.return_status?.toLowerCase() !== 'returned' ||
                        deviceStatus.is_returned === false;

                    return {
                        title: getDeviceStatusTitle(deviceStatus),
                        timestamp: getDeviceTimestamp(
                            deviceStatus.device_status,
                            deviceStatus.status_timestamp
                        ),
                        alert: hasUnreturnedDevice,
                        ...(exchangeDetails.length > 0 && {
                            details: exchangeDetails
                        })
                    };
                }

                // For other statuses, use regular fulfillment details
                const fulfillmentDetails = createFulfillmentDetails(
                    deviceStatus.fulfillment_details
                );

                return {
                    title: getDeviceStatusTitle(deviceStatus),
                    timestamp: getDeviceTimestamp(
                        deviceStatus.device_status,
                        deviceStatus.status_timestamp
                    ),
                    ...(fulfillmentDetails.length > 0 && {
                        details: [fulfillmentDetails]
                    })
                };
            });
    }, [currentDevice]);
};
